import { FC, useState, useMemo, memo } from 'react';
import Upload, {UploadProps, UploadChangeParam, UploadFile} from 'antd/lib/upload';
import { useTranslation } from 'react-i18next';

import { apiConfig } from '../../../../../config/server/server.index';
import PrimaryButtonLoading from '~src/shared/buttons/loadingButtons/PrimaryButtonLoading';

import { UploadBox, ErrorBox } from './integrationFileUpload.sc';
import { routes } from '~src/routingConfig';
import {ImportStatusResp} from "~src/services/fetch/import.service";
import {ErrResp, isErrResp} from "~src/shared/errors/interfaces";

interface OwnProps {
  integrationId: string;
  uploadError?: string;
  startImportStatusCheck(importId: string): void;
}

type Props = OwnProps;

const IntegrationFileUpload: FC<Props> = memo(function IntegrationFileUpload(props)  {
  const [t] = useTranslation();
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadingError, setIsUploadingError] = useState(false);
  const [importId, setImportId] = useState<string | null>(null); // Store importId to generate link
  const [statusCode, setStatusCode] = useState<number | null>(null); // Capture status code

  const uploadConfig = useMemo<UploadProps>(
    () => ({
      accept: '.csv',
      action: `${apiConfig.userServiceApi.base_url}/imports`,
      headers: { Authorization: `Bearer ${localStorage.getItem('jwt')}` },
      data: { integration_id: props.integrationId },
    }),
    [props.integrationId],
  );

  const onUploadChange = ({ file }: UploadChangeParam<UploadFile<ImportStatusResp|ErrResp>>) => {
    switch (file.status) {
      case 'uploading':
        setIsUploading(true);
        break;
      case 'done':
        setIsUploading(false);
        if (isErrResp(file.response)) {
          setIsUploadingError(true);
          break;
        }
        if (!file.response) {
          setIsUploadingError(true);
          throw new Error('unexpected error: upload is done, but we have no response!')
        }
        props.startImportStatusCheck(file.response.id);
        break;
      case 'error':
        if (Array.isArray(file.response) && file.response.length > 0) {
          const response = file.response[0];
          if (response.translation_key === 'running_import_found') {
            setStatusCode(409);
            setImportId(response.arguments.id);
          }
        }
        break;
    }
  };

  const uploadErrorKey: string = props.uploadError || 'x.company.import.uploading_error';
  const importLink = `${routes.company.importUsers}/${importId}`;

  return (
    <UploadBox>
      <Upload {...uploadConfig} onChange={onUploadChange} showUploadList={false}>
        {statusCode !== 409 && (
          <PrimaryButtonLoading data-translation="x.company.import.select_file_button" isLoading={isUploading}>
            {t('x.company.import.select_file_button')}
          </PrimaryButtonLoading>
        )}
      </Upload>
      {!isUploading && isUploadingError && (
        <ErrorBox data-translation={uploadErrorKey}>{t(uploadErrorKey)}</ErrorBox>
      )}
      {statusCode === 409 && (
        <a href={importLink} data-translation="x.company.import.cancel_running_import">
          {t('x.company.import.cancel_running_import')}
        </a>
      )}
    </UploadBox>
  );
});

export default IntegrationFileUpload;
